/**
 * TransferReportsSummaryStep View Component
 *
 * Vertical From → To layout showing full player names without truncation.
 * Consolidates counts into a single summary line for cleaner hierarchy.
 * Works consistently across iOS, Android, and web.
 * @module features/player-detail/components/TransferReportsSummaryStep
 */

import { ArrowDown, ClipboardList, FileText } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Theme type for lookup tables */
export type Theme = "dark" | "light";

/** Props for TransferReportsSummaryStepView */
export interface TransferReportsSummaryStepViewProps {
  /** Source manual player name */
  readonly sourcePlayerName: string;
  /** Source player position abbreviation */
  readonly sourcePositionAbbreviation: string | null;
  /** Source player team name */
  readonly sourceTeamName: string | null;
  /** Target player display name */
  readonly targetDisplayName: string;
  /** Target player position abbreviation */
  readonly targetPositionAbbreviation: string | null;
  /** Target player team name */
  readonly targetTeamName: string | null;
  /** Number of scout reports to transfer */
  readonly scoutReportCount: number;
  /** Number of notes to transfer */
  readonly noteCount: number;
  /** Resolved theme for lookup tables */
  readonly theme: Theme;
  /** Callback fired when "Transfer All" button is pressed */
  readonly onTransferAll: () => void;
  /** Callback fired when back button is pressed */
  readonly onBack: () => void;
}

/**
 * Vertical From → To summary with full player names and consolidated counts
 * @param root0 - Component props
 * @param root0.sourcePlayerName - Source manual player name
 * @param root0.sourcePositionAbbreviation - Source position abbreviation
 * @param root0.sourceTeamName - Source team name
 * @param root0.targetDisplayName - Target player display name
 * @param root0.targetPositionAbbreviation - Target position abbreviation
 * @param root0.targetTeamName - Target team name
 * @param root0.scoutReportCount - Number of scout reports
 * @param root0.noteCount - Number of notes
 * @param root0.onTransferAll - Transfer All button handler
 * @param root0.onBack - Back button handler
 */
const TransferReportsSummaryStepView = ({
  sourcePlayerName,
  sourcePositionAbbreviation,
  sourceTeamName,
  targetDisplayName,
  targetPositionAbbreviation,
  targetTeamName,
  scoutReportCount,
  noteCount,
  onTransferAll,
  onBack,
}: TransferReportsSummaryStepViewProps) => (
  <Stack className="gap-5 p-5">
    {/* From → To Vertical Card */}
    <Stack className="items-center gap-0">
      {/* From Player */}
      <Box className="border-border-outline-subtle w-full rounded-md border bg-surface-subtle p-4">
        <Stack className="gap-1">
          <Text
            variant="caption"
            className="font-medium uppercase tracking-wide text-content-muted"
          >
            From
          </Text>
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="title-sm" className="flex-1 text-content-primary">
              {sourcePlayerName}
            </Text>
            {sourcePositionAbbreviation ? (
              <Box className="rounded-sm bg-status-warning-surface px-1 py-micro">
                <Text
                  variant="caption"
                  className="font-medium text-status-warning"
                >
                  {sourcePositionAbbreviation}
                </Text>
              </Box>
            ) : null}
          </Stack>
          {sourceTeamName ? (
            <Text variant="body" className="text-content-secondary">
              {sourceTeamName}
            </Text>
          ) : null}
        </Stack>
      </Box>

      {/* Arrow Connector */}
      <Box className="size-8 items-center justify-center rounded-full bg-status-warning-surface">
        <Icon as={ArrowDown} size="sm" className="text-status-warning" />
      </Box>

      {/* To Player */}
      <Box className="border-border-outline-subtle w-full rounded-md border bg-surface-subtle p-4">
        <Stack className="gap-1">
          <Text
            variant="caption"
            className="font-medium uppercase tracking-wide text-content-muted"
          >
            To
          </Text>
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="title-sm" className="flex-1 text-content-primary">
              {targetDisplayName}
            </Text>
            {targetPositionAbbreviation ? (
              <Box className="rounded-sm bg-status-warning-surface px-1 py-micro">
                <Text
                  variant="caption"
                  className="font-medium text-status-warning"
                >
                  {targetPositionAbbreviation}
                </Text>
              </Box>
            ) : null}
          </Stack>
          {targetTeamName ? (
            <Text variant="body" className="text-content-secondary">
              {targetTeamName}
            </Text>
          ) : null}
        </Stack>
      </Box>
    </Stack>

    {/* Item Counts with Icons */}
    <Stack direction="horizontal" className="items-center gap-5">
      <Stack direction="horizontal" className="items-center gap-2">
        <Box className="size-8 items-center justify-center rounded-md bg-status-warning-surface">
          <Icon as={ClipboardList} size="sm" className="text-status-warning" />
        </Box>
        <Text variant="body-strong" className="text-content-primary">
          {scoutReportCount} report{scoutReportCount !== 1 ? "s" : ""}
        </Text>
      </Stack>
      <Stack direction="horizontal" className="items-center gap-2">
        <Box className="size-8 items-center justify-center rounded-md bg-status-warning-surface">
          <Icon as={FileText} size="sm" className="text-status-warning" />
        </Box>
        <Text variant="body-strong" className="text-content-primary">
          {noteCount} note{noteCount !== 1 ? "s" : ""}
        </Text>
      </Stack>
    </Stack>

    {/* Action Buttons */}
    <Stack className="gap-3 pt-2">
      <Button
        label="Transfer All"
        tone="primary"
        size="lg"
        labelTone="on-accent"
        onPress={onTransferAll}
        testID="transfer-all-button"
      />

      <Button
        label="Back to Search"
        variant="outline"
        tone="secondary"
        size="md"
        labelTone="secondary"
        onPress={onBack}
        testID="transfer-summary-back"
      />
    </Stack>
  </Stack>
);

TransferReportsSummaryStepView.displayName = "TransferReportsSummaryStepView";

export default memo(TransferReportsSummaryStepView);
